import { useEffect, useRef, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Leaf, Truck, Building, FileDown, Loader2, ShieldCheck, Mail, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const RATING_STYLE = {
  High: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-rose-50 text-rose-700 border-rose-200",
};

const RatingBadge = ({ rating }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold ${RATING_STYLE[rating] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
    {rating}
  </span>
);

const _defaultFY = (() => {
  const d = new Date();
  const end = d.getMonth() + 1 >= 7 ? d.getFullYear() + 1 : d.getFullYear();
  return `FY${end % 100}`;
})();

const _fyToBounds = (f) => {
  const yr = f && f.startsWith("FY") ? parseInt(f.slice(2), 10) : 26;
  return { start: `${2000 + yr - 1}-07`, end: `${2000 + yr}-06` };
};

// Australian FY quarters: Q1 Jul–Sep, Q2 Oct–Dec, Q3 Jan–Mar, Q4 Apr–Jun.
const _fyQuarter = (f, q) => {
  const yr = f && f.startsWith("FY") ? parseInt(f.slice(2), 10) : 26;
  const y1 = 2000 + yr - 1, y2 = 2000 + yr;
  return { 1: [`${y1}-07`, `${y1}-09`], 2: [`${y1}-10`, `${y1}-12`],
           3: [`${y2}-01`, `${y2}-03`], 4: [`${y2}-04`, `${y2}-06`] }[q];
};

// The Australian FY a given YYYY-MM month falls in (July starts the next FY).
const _fyOfMonth = (pm) => {
  const y = parseInt(pm.slice(0, 4), 10), m = parseInt(pm.slice(5, 7), 10);
  return `FY${((m >= 7 ? y + 1 : y) - 2000 + 100) % 100}`;
};

const _validMonth = (v) => {
  if (!v || !/^\d{4}-\d{2}$/.test(v)) return false;
  const y = parseInt(v.slice(0, 4), 10);
  return y >= 2015 && y <= 2100;
};

const _savedPeriod = (client) => {
  if (!client) return null;
  try {
    const s = JSON.parse(localStorage.getItem(`gbe_s3_period_${client}`) || "null");
    if (s && _validMonth(s.fromM) && _validMonth(s.toM)) return s;
  } catch { /* ignore */ }
  return null;
};

const _savePeriod = (client, fromM, toM) => {
  if (!client) return;
  try { localStorage.setItem(`gbe_s3_period_${client}`, JSON.stringify({ fromM, toM })); }
  catch { /* ignore */ }
};

const _fyOptions = (() => {
  const d = new Date();
  const cur = d.getMonth() + 1 >= 7 ? d.getFullYear() + 1 : d.getFullYear();
  const arr = [];
  for (let y = cur + 1; y >= cur - 3; y--) arr.push(`FY${y % 100}`);
  return arr;
})();

export function ClientScope3Panel({ clientName, fy = _defaultFY }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [fromM, setFromM] = useState(() => _savedPeriod(clientName)?.fromM || _fyToBounds(fy).start);
  const [toM, setToM] = useState(() => _savedPeriod(clientName)?.toM || _fyToBounds(fy).end);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const reqId = useRef(0);
  const prevFy = useRef(fy);

  const applyPeriod = (fromV, toV) => { setFromM(fromV); setToM(toV); _savePeriod(clientName, fromV, toV); };

  // Snap to the page-level FY only when it genuinely changes (StrictMode-safe).
  useEffect(() => {
    if (prevFy.current === fy) return;
    prevFy.current = fy;
    const b = _fyToBounds(fy);
    applyPeriod(b.start, b.end);
    // eslint-disable-next-line
  }, [fy]);

  // Reopen each client on their last-used reporting period (or that client's FY).
  useEffect(() => {
    const s = _savedPeriod(clientName);
    if (s) { setFromM(s.fromM); setToM(s.toM); }
    else { const b = _fyToBounds(fy); setFromM(b.start); setToM(b.end); }
    // eslint-disable-next-line
  }, [clientName]);

  // The FY preset this range matches exactly (if any) — else it's a custom range.
  const matchedFy = _fyOptions.find((f) => {
    const b = _fyToBounds(f);
    return b.start === fromM && b.end === toM;
  }) || null;
  const isCustom = !matchedFy;

  const setFrom = (v) => { if (!_validMonth(v)) return; applyPeriod(v, v > toM ? v : toM); };
  const setTo = (v) => { if (!_validMonth(v)) return; applyPeriod(v < fromM ? v : fromM, v); };

  const params = () => {
    const p = new URLSearchParams({ fy: matchedFy || fy });
    if (clientName) p.set("client", clientName);
    if (isCustom) { p.set("start", fromM); p.set("end", toM); }
    return p;
  };

  useEffect(() => {
    const id = ++reqId.current;
    setLoading(true); setErr(null);
    api.get(`/sustainability/client-scope3?${params()}`, { timeout: 90000 })
      .then((r) => { if (id === reqId.current) { setData(r.data); setErr(null); } })
      .catch((e) => { if (id === reqId.current) setErr(e.response?.data?.detail
        || (e.code === "ECONNABORTED" ? "Scope 3 is taking longer than expected — please try again in a moment"
            : "Failed to load Scope 3 data")); })
      .finally(() => { if (id === reqId.current) setLoading(false); });
    // eslint-disable-next-line
  }, [clientName, fy, fromM, toM]);

  const setFy = (f) => { const b = _fyToBounds(f); applyPeriod(b.start, b.end); };
  const setQuarter = (q) => { const r = _fyQuarter(_fyOfMonth(fromM), q); if (r) applyPeriod(r[0], r[1]); };

  const sendEmail = async () => {
    if (!emailTo.includes("@")) { toast.error("Enter a valid recipient email"); return; }
    setEmailSending(true);
    try {
      const body = { fy: matchedFy || fy, to_email: emailTo.trim(), message: emailMsg.trim() || undefined };
      if (clientName) body.client = clientName;
      if (isCustom) { body.start = fromM; body.end = toM; }
      const { data: res } = await api.post("/sustainability/client-scope3/email", body, { timeout: 90000 });
      toast.success(`Scope 3 statement emailed to ${res.to}`);
      setEmailOpen(false); setEmailMsg("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setEmailSending(false); }
  };

  const downloadCert = async () => {
    setDownloading(true);
    try {
      const { data: blob } = await api.get(`/sustainability/client-scope3.pdf?${params()}`,
        { responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const tag = (data?.fy || fy).replace(/[^a-z0-9]+/gi, "_");
      a.download = `GBE_Scope3_Statement_${(data?.client || clientName || "client").replace(/[^a-z0-9]+/gi, "_")}_${tag}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Scope 3 statement downloaded");
    } catch {
      toast.error("Failed to generate certificate");
    } finally { setDownloading(false); }
  };

  if (err) return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">{err}</div>;
  if (!data) return <div className="h-56 rounded-lg bg-white border border-slate-200 shimmer" />;

  const t = data.totals || {};
  return (
    <div className="space-y-4" data-testid="client-scope3-panel">
      {data.fy_fallback && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-xs text-amber-800" data-testid="scope3-fy-fallback">
          No {data.fy_requested} sustainability data has been recorded yet — showing the latest complete year ({data.fy}).
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5" data-testid="scope3-period-controls">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Reporting period</span>
        <select value={matchedFy || "__custom"}
                onChange={(e) => { if (e.target.value !== "__custom") setFy(e.target.value); }}
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                data-testid="scope3-fy-preset">
          {_fyOptions.map((f) => <option key={f} value={f}>{f}</option>)}
          {!matchedFy && <option value="__custom">Custom range</option>}
        </select>
        <span className="text-xs text-slate-400">or</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          From
          <input type="month" value={fromM} max={toM} min="2015-01"
                 onChange={(e) => setFrom(e.target.value)}
                 className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                 data-testid="scope3-from-month" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          To
          <input type="month" value={toM} min={fromM} max="2100-12"
                 onChange={(e) => setTo(e.target.value)}
                 className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700"
                 data-testid="scope3-to-month" />
        </label>
        {isCustom && (
          <button onClick={() => setFy(fy)}
                  className="h-8 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-500 hover:bg-slate-100"
                  data-testid="scope3-reset-period">Reset to {fy}</button>
        )}
        <div className="flex items-center gap-1" data-testid="scope3-quarter-picks">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-0.5">Quarter</span>
          {[1, 2, 3, 4].map((q) => (
            <button key={q} onClick={() => setQuarter(q)}
                    className="h-7 w-7 rounded-md border border-slate-200 bg-white text-[11px] font-medium text-slate-600 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700"
                    data-testid={`scope3-quarter-${q}`}>Q{q}</button>
          ))}
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500">
          {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
          Showing <b className="text-slate-700">{data.fy}</b>
        </span>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Data quality:</span>
          <RatingBadge rating={data.overall_rating} />
          <span className="text-[11px] text-slate-400">Hybrid model v2 · basis: {
            data.basis === "invoiced_revenue" ? "invoiced revenue (actual billing)"
            : data.basis === "job_value" ? "job value (expenditure)" : "insufficient data"}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="scope3-email-cert">
            <Mail className="w-3.5 h-3.5 mr-1.5" />Email to client
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCert} disabled={downloading}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="scope3-download-cert">
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
            Download Scope 3 statement (PDF)
          </Button>
        </div>
      </div>

      <Tabs defaultValue="attribution">
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger value="attribution" data-testid="scope3-tab-attribution" className="text-xs data-[state=active]:bg-white">Attribution</TabsTrigger>
          <TabsTrigger value="methodology" data-testid="scope3-tab-methodology" className="text-xs data-[state=active]:bg-white">Methodology</TabsTrigger>
        </TabsList>

        <TabsContent value="attribution" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
              <div className="flex items-center gap-1.5 text-emerald-800"><Leaf className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">Total Scope 3 ({data.fy})</span></div>
              <div className="text-2xl font-display font-semibold text-emerald-900 mt-1 tabular-nums" data-testid="scope3-total">
                {t.scope3_tonnes ?? 0} t CO₂e
              </div>
              {data.comparison && data.comparison.delta_pct != null && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px] font-medium" data-testid="scope3-yoy">
                  {data.comparison.delta_pct > 0
                    ? <TrendingUp className="w-3.5 h-3.5 text-rose-600" />
                    : data.comparison.delta_pct < 0
                      ? <TrendingDown className="w-3.5 h-3.5 text-emerald-600" />
                      : <Minus className="w-3.5 h-3.5 text-slate-400" />}
                  <span className={data.comparison.delta_pct > 0 ? "text-rose-600" : data.comparison.delta_pct < 0 ? "text-emerald-600" : "text-slate-500"}>
                    {data.comparison.delta_pct > 0 ? "+" : ""}{data.comparison.delta_pct}%
                  </span>
                  <span className="text-slate-400 font-normal">vs {data.comparison.prev_label} ({data.comparison.prev_scope3_tonnes} t)</span>
                  {data.comparison.incomplete && (
                    <span className="text-amber-600 font-normal" title="Only months with data are compared, like-for-like">· partial period</span>
                  )}
                </div>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-1.5 text-slate-500"><Truck className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">Direct — service delivery</span></div>
              <div className="text-2xl font-display font-semibold text-slate-900 mt-1 tabular-nums">{t.direct_tonnes ?? 0} t</div>
              <div className="mt-1"><RatingBadge rating="Medium" /></div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-1.5 text-slate-500"><Building className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-wider font-medium">Allocated overheads</span></div>
              <div className="text-2xl font-display font-semibold text-slate-900 mt-1 tabular-nums">{t.overhead_tonnes ?? 0} t</div>
              <div className="mt-1"><RatingBadge rating="Low" /></div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Your share of GBE activity</div>
              <div className="text-2xl font-display font-semibold text-slate-900 mt-1 tabular-nums">{data.service_share_pct}%</div>
              <div className="text-[11px] text-slate-400 mt-0.5">{data.basis === "invoiced_revenue" ? "by invoiced revenue" : "by job value delivered"}</div>
            </div>
          </div>

          {data.insufficient_data ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-5 text-sm text-amber-800" data-testid="scope3-insufficient">
              Insufficient activity data — no job value data was found for this period, so no attribution has been
              made. Figures show zero rather than a fabricated share.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-5">
              <h3 className="font-display text-base font-semibold text-slate-900">Monthly attribution</h3>
              <p className="text-xs text-slate-500 mb-3">kg CO₂e allocated to {data.client} per month — direct service delivery vs allocated overheads</p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.months} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v, n) => [`${fmtNumber(Math.round(v))} kg CO₂e`,
                      n === "direct_kg" ? "Direct (service delivery)" : "Allocated overheads"]} />
                    <Legend formatter={(v) => v === "direct_kg" ? "Direct (service delivery)" : "Allocated overheads"}
                            wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="direct_kg" stackId="s3" fill="#059669" />
                    <Bar dataKey="overhead_kg" stackId="s3" fill="#0284c7" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <p className="text-[11px] text-slate-400 leading-relaxed" data-testid="scope3-disclosure">{data.disclosure}</p>
        </TabsContent>

        <TabsContent value="methodology" className="mt-4 space-y-4" data-testid="scope3-methodology-tab">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-gbe" />
              <h3 className="font-display text-base font-semibold text-slate-900">Hybrid attribution model (v2)</h3>
            </div>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">{data.methodology}</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-600 font-medium">Attribution components</div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-3 py-2">Component</th>
                  <th className="text-left font-medium px-3 py-2">Emission source</th>
                  <th className="text-left font-medium px-3 py-2">Allocation basis</th>
                  <th className="text-left font-medium px-3 py-2">Rating</th>
                  <th className="text-right font-medium px-3 py-2">t CO₂e</th>
                </tr>
              </thead>
              <tbody>
                {(data.components || []).map((c, i) => (
                  <tr key={i} className="border-b border-slate-100 align-top">
                    <td className="px-3 py-2 text-slate-800 font-medium max-w-[220px]">{c.name}</td>
                    <td className="px-3 py-2 text-slate-600">{c.source}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[260px]">{c.allocation}</td>
                    <td className="px-3 py-2"><RatingBadge rating={c.rating} /></td>
                    <td className="px-3 py-2 text-right tabular-nums">{((c.kg || 0) / 1000).toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-600 font-medium">Data-quality rating scale</div>
            <table className="w-full text-sm">
              <tbody>
                {(data.rating_table || []).map((r) => (
                  <tr key={r.rating} className="border-b border-slate-100">
                    <td className="px-3 py-2 w-24"><RatingBadge rating={r.rating} /></td>
                    <td className="px-3 py-2 text-slate-600">{r.basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Currently excluded (no per-client source data yet)</div>
            <ul className="mt-1.5 text-xs text-slate-600 list-disc pl-4 space-y-0.5">
              {(data.excluded || []).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
            <p className="text-[11px] text-slate-400 mt-2">Adding AroFlo purchasing and timesheet data will move these into the Directly Attributable component and lift the rating to High.</p>
          </div>

          <p className="text-[11px] text-slate-400 leading-relaxed">{data.disclosure}</p>
        </TabsContent>
      </Tabs>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="scope3-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Scope 3 statement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Sends the <b>{data.fy}</b> Scope 3 emissions statement for <b>{data.client}</b> as a PDF attachment.
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Recipient email</label>
              <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                     placeholder="contact@client.com.au" data-testid="scope3-email-to" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-600">Message (optional)</label>
              <Textarea value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} rows={3}
                        placeholder="Add a short note to include in the email…" data-testid="scope3-email-msg" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailOpen(false)} disabled={emailSending}>Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="scope3-email-send">
              {emailSending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
              Send statement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
